import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ArrowRight, Shield } from "lucide-react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_cercle-local-bhealth/artifacts/csfd9ojj_logofinal-logo-full-color-rgb-900px-w-72ppi.png";
const BG = "https://static.prod-images.emergentagent.com/jobs/f68fc452-2241-43df-a388-a567f77ab83b/images/edc1f49f20a9addff6523bdccbb6c4ffe0332a6a77a549c65e0b0b239846fe27.png";

export default function AdminLoginPage({ merchantMode = false }) {
  const { login, user } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const nav = useNavigate();

  React.useEffect(() => {
    if (user && user.user_id) {
      if (user.role === "merchant") nav("/partenaire", { replace: true });
      else nav("/admin", { replace: true });
    }
  }, [user, nav]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const data = await login(email, password);
      toast.success("Bienvenue !");
      if (data.role === "merchant") nav("/partenaire");
      else nav("/admin");
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Identifiants incorrects");
    } finally {
      setLoading(false);
    }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + (merchantMode ? "/partenaire" : "/admin");
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="relative hidden lg:block">
        <img src={BG} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-br from-navy-950/90 via-navy-900/80 to-navy-900/70" />
        <div className="relative h-full flex flex-col p-12 text-white">
          <Link to="/" className="flex items-center gap-3">
            <img src={LOGO_URL} alt="B.health®" className="h-12 w-12 object-contain" />
            <div>
              <div className="font-heading font-extrabold text-lg uppercase tracking-wider">Local Benefits</div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-white/50 font-semibold">Admin · by B.health<sup>®</sup></div>
            </div>
          </Link>
          <div className="mt-auto">
            <div className="inline-block chip bg-lime-500 text-navy-900 mb-4">Espace sécurisé</div>
            <h1 className="font-heading font-black text-5xl leading-[0.95] tracking-tighter">Pilotez votre<br/>cercle local.</h1>
            <p className="mt-5 text-white/70 max-w-md">Validez les partenaires, ajustez le compteur, pilotez l'affichage. Tout est ici.</p>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center p-8 bg-white">
        <div className="w-full max-w-md">
          <div className="lg:hidden mb-8 flex items-center gap-3">
            <img src={LOGO_URL} alt="B.health®" className="h-10 w-10 object-contain" />
            <div className="font-heading font-extrabold uppercase tracking-wider text-navy-900">Local Benefits</div>
          </div>
          <div className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-navy-900/60 font-semibold mb-4">
            <Shield className="w-3.5 h-3.5" /> {merchantMode ? "Espace partenaire" : "Connexion admin"}
          </div>
          <h2 className="font-heading font-extrabold text-4xl text-navy-900 tracking-tighter" data-testid="admin-login-title">Se connecter</h2>
          <p className="mt-3 text-muted-foreground">
            {merchantMode
              ? "Accédez à votre espace partenaire B.health® Local Benefits."
              : "Accédez au panneau d'administration B.health®."}
          </p>

          <form onSubmit={submit} className="mt-8 space-y-4" data-testid="admin-login-form">
            <div>
              <Label className="text-xs uppercase tracking-wider font-semibold text-navy-900/70">Email</Label>
              <Input data-testid="admin-login-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1.5 h-12" placeholder="sante@bhealth.be" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider font-semibold text-navy-900/70">Mot de passe</Label>
              <Input data-testid="admin-login-password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className="mt-1.5 h-12" placeholder="••••••••" />
            </div>
            <button type="submit" disabled={loading} className="btn-navy w-full h-12 disabled:opacity-60" data-testid="admin-login-submit">
              {loading ? "Connexion…" : "Se connecter"} <ArrowRight className="w-4 h-4" />
            </button>
            <div className="text-right">
              <Link
                to={merchantMode ? "/partenaire/mot-de-passe-oublie" : "/admin/mot-de-passe-oublie"}
                className="text-xs text-navy-900/60 hover:text-navy-900 font-semibold"
                data-testid="forgot-password-link"
              >
                Mot de passe oublié ?
              </Link>
            </div>
          </form>

          <div className="relative my-6 flex items-center">
            <div className="flex-1 h-px bg-navy-900/10" />
            <span className="px-3 text-xs uppercase tracking-wider text-muted-foreground font-semibold">ou</span>
            <div className="flex-1 h-px bg-navy-900/10" />
          </div>

          <button onClick={googleLogin} className="w-full h-12 rounded-full border border-navy-900/10 hover:bg-navy-900/5 transition flex items-center justify-center gap-3 font-semibold text-navy-900" data-testid="admin-login-google">
            <svg className="w-5 h-5" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
            Connexion avec Google
          </button>

          <p className="mt-8 text-xs text-muted-foreground text-center">
            Pas d'accès ? <Link to="/" className="text-navy-900 font-semibold hover:underline">Retour à l'accueil</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
